<template>
  <div class="supermarket-summary-core" :class="{ shared: shareVersion }">
    <div class="top-part">
      <div class="title" v-if="!shareVersion">{{ title }}</div>
      <div class="title" v-if="shareVersion">{{ sharedTitle }}</div>
      <img class="icons" :src="require('@/assets/insights/groceries-benchmark-icons.svg')"/>
    </div>
    <div class="bottom-part">
      <div class="summary" v-if="!shareVersion">
        <span>בשלושת החודשים האחרונים הוצאת בממוצע</span>
        <span class="amount">{{ formatAmountWithFractionAndCurrency(average) }}</span>
        <span>על סופר.</span>
        <template v-if="benchmarkType === 'reinforce'">
          <span>כל הכבוד!</span>
        </template>
        <template v-if="benchmarkType === 'improve'">
          <span>צמצום של</span>
          <span class="amount">כ-{{ formatAmountWithFractionAndCurrency(improvement) }}</span>
          <span>בחודש ישפר את המיקום שלך משמעותית.</span>
        </template>
      </div>
      <div class="summary" v-else>
        <span>בשלושת החודשים האחרונים הוצאתי בממוצע</span>
        <span class="amount">{{ formatAmountWithFractionAndCurrency(average) }}</span>
        <span>על סופר.</span>
        <template v-if="benchmarkType === 'reinforce'">
          <span>כל הכבוד!</span>
        </template>
        <template v-if="benchmarkType === 'improve'">
          <span>צמצום של</span>
          <span class="amount">כ-{{ formatAmountWithFractionAndCurrency(improvement) }}</span>
          <span>בחודש ישפר את המיקום שלי משמעותית.</span>
        </template>
      </div>
      <bar-graph :graph-height="graphHeight" :bar-values="barValues" :with-sign="false"></bar-graph>
    </div>
  </div>
</template>

<script>
import TransactionDisplayUtils
  from '@/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/cashflow-view/generic-components/TransactionDisplayUtils';
import BarGraph from '@/base-components/BarGraph';
import _ from 'lodash';
import dateUtils from '@/utils/dates';

export default {
  name: 'SupermarketSummaryCore',
  mixins: [TransactionDisplayUtils],
  components: {
    BarGraph,
  },
  props: {
    benchmarkType: {
      type: String,
      required: true,
    },
    improvement: {
      type: Number,
      required: true,
    },
    supermarketSummary: {
      type: Object,
      required: true,
    },
    supermarketGraphHistory: {
      type: Array,
      required: true,
    },
    shareVersion: {
      type: Boolean,
      default: () => false,
    },
  },
  computed: {
    average() {
      return _.chain(this.supermarketGraphHistory).slice(0, 3).meanBy(b => b.total).value();
    },
    title() {
      if (this.benchmarkType === 'reinforce') {
        const start = 'וואו! הוצאת בסופר פחות ממה ש-';
        const end = '% מהלקוחות שדומים לך הוציאו';
        return `${start}${this.supermarketSummary.above}${end}`;
      }
      if (this.benchmarkType === 'improve') {
        const start = 'הוצאת בסופר יותר ממה ש-';
        const end = '% מהלקוחות שדומים לך הוציאו.';
        return `${start}${this.supermarketSummary.below}${end}`;
      }
      return 'הוצאות הסופר שלך בחודשים האחרונים';
    },
    sharedTitle() {
      if (this.benchmarkType === 'reinforce') {
        const start = 'וואו! הוצאתי בסופר פחות ממה ש-';
        const end = '% מהלקוחות שדומים לי הוציאו';
        return `${start}${this.supermarketSummary.above}${end}`;
      }
      if (this.benchmarkType === 'improve') {
        const start = 'הוצאתי בסופר יותר ממה ש-';
        const end = '% מהלקוחות שדומים לי הוציאו. אבל אני על זה!';
        return `${start}${this.supermarketSummary.below}${end}`;
      }
      return 'הוצאות הסופר שלי בחודשים האחרונים';
    },
    graphHeight() {
      return 60;
    },
    barValues() {
      return _.map(this.supermarketGraphHistory, ({ total, budgetDate }, index) => {
        return {
          name: this.formatMonthName(budgetDate),
          value: total,
          cssClasses: { 'grayed-out': index > 2 },
        };
      });
    },
  },
  methods: {
    formatMonthName(budgetDate) {
      return dateUtils.getShortHebrewMonth(budgetDate);
    },
  },
};

</script>

<style lang="scss">
@import '~base-ui/src/scss/riseup-colors';

.supermarket-summary-core {
  &.shared {
    .top-part {
      background-color: $riseup_light_blue;
      .title {
        color: $riseup_blue;
        padding: 36px 42px 0;
      }
    }
    .bottom-part {
      padding: 18px 42px 42px;
    }
  }

  .top-part {
    display: flex;
    flex-direction: column;
    background-color: $riseup_blue;
    border-radius: 8px 8px 0 0;
    .title {
      padding: 36px 18px 0 18px;
      font-size: 22px;
      line-height: 28px;
      font-weight: bold;
      margin-bottom: 18px;
      color: $riseup_white;
    }
    .icons {
      height: 73px;
      width: 96%;
    }
  }

  .bottom-part {
    padding: 18px;
  }

  .summary {
    color: $riseup_black;
    font-size: 18px;
    line-height: 25px;
    .amount {
      font-weight: bold;
      direction: rtl;
      white-space: nowrap;
    }
  }

  .bar-graph .graph-drawing .bars .grayed-out .bar {
    background-color: $riseup_light_blue;
  }
}

</style>
